/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Body_list_cashflow_ids_for_ledger } from '../models/Body_list_cashflow_ids_for_ledger';
import type { Body_list_cashflows_for_ledger_with_relations_paginated } from '../models/Body_list_cashflows_for_ledger_with_relations_paginated';
import type { BulkAssignFromRelationshipsRequest } from '../models/BulkAssignFromRelationshipsRequest';
import type { BulkDeleteCashflowsRequest } from '../models/BulkDeleteCashflowsRequest';
import type { BulkSetEntityAssociationRequest } from '../models/BulkSetEntityAssociationRequest';
import type { CashflowCreateWithRelations } from '../models/CashflowCreateWithRelations';
import type { CashflowExportColumnsResponse } from '../models/CashflowExportColumnsResponse';
import type { CashflowExportRequest } from '../models/CashflowExportRequest';
import type { CashflowExportResponse } from '../models/CashflowExportResponse';
import type { CashflowExportTemplateCreate } from '../models/CashflowExportTemplateCreate';
import type { CashflowExportTemplateRead } from '../models/CashflowExportTemplateRead';
import type { CashflowExportTemplateUpdate } from '../models/CashflowExportTemplateUpdate';
import type { CashflowExternalIdentifierTypeMutate } from '../models/CashflowExternalIdentifierTypeMutate';
import type { CashflowExternalIdentifierTypeWithLedgers } from '../models/CashflowExternalIdentifierTypeWithLedgers';
import type { CashflowFileImportRequest } from '../models/CashflowFileImportRequest';
import type { CashflowLedgerCreateRequest } from '../models/CashflowLedgerCreateRequest';
import type { CashflowLedgerRead } from '../models/CashflowLedgerRead';
import type { CashflowRead } from '../models/CashflowRead';
import type { CashflowScenarioCreate } from '../models/CashflowScenarioCreate';
import type { CashflowScenarioRead } from '../models/CashflowScenarioRead';
import type { CashflowTagCategoryCreateWithValues } from '../models/CashflowTagCategoryCreateWithValues';
import type { CashflowTagCategoryRead } from '../models/CashflowTagCategoryRead';
import type { CashflowTransactionTypeCreate } from '../models/CashflowTransactionTypeCreate';
import type { CashflowTransactionTypeRead } from '../models/CashflowTransactionTypeRead';
import type { CashflowUpdateWithRelations } from '../models/CashflowUpdateWithRelations';
import type { EntityAssociationData } from '../models/EntityAssociationData';
import type { FieldDefinition } from '../models/FieldDefinition';
import type { ImportResult } from '../models/ImportResult';
import type { NamespaceCoverage } from '../models/NamespaceCoverage';
import type { PaginatedResponse } from '../models/PaginatedResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class CashflowsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Update Cashflow Ledger
     * Update a cashflow ledger.
     * @param ledgerId
     * @param clusterId
     * @param requestBody
     * @returns CashflowLedgerRead Successful Response
     * @throws ApiError
     */
    public updateCashflowLedger(
        ledgerId: string,
        clusterId: string,
        requestBody: CashflowLedgerCreateRequest,
    ): CancelablePromise<CashflowLedgerRead> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/cashflows/update-cashflow-ledger/{ledger_id}',
            path: {
                'ledger_id': ledgerId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Cashflow Ledger
     * Delete a cashflow ledger.
     * @param ledgerId
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteCashflowLedger(
        ledgerId: string,
        clusterId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/cashflows/delete-cashflow-ledger/{ledger_id}',
            path: {
                'ledger_id': ledgerId,
            },
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Delete Cashflows
     * Bulk delete cashflows by their IDs.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkDeleteCashflows(
        clusterId: string,
        requestBody: BulkDeleteCashflowsRequest,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/cashflows/bulk-delete-cashflows',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Set Entity Association
     * Bulk-set entity association for a namespace on multiple cashflows.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkSetEntityAssociation(
        clusterId: string,
        requestBody: BulkSetEntityAssociationRequest,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/cashflows/bulk-set-entity-association',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Assign From Relationships
     * Auto-fill missing entity associations by walking the relationship graph.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkAssignFromRelationships(
        clusterId: string,
        requestBody: BulkAssignFromRelationshipsRequest,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/cashflows/bulk-assign-from-relationships',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflow Ids For Ledger
     * List cashflow IDs matching filter criteria (for Select All support).
     * @param ledgerId
     * @param clusterId
     * @param search
     * @param scenarioId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public listCashflowIdsForLedger(
        ledgerId: string,
        clusterId: string,
        search: string = '',
        scenarioId?: string,
        requestBody?: Body_list_cashflow_ids_for_ledger,
    ): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/cashflows/list-cashflow-ids-for-ledger/{ledger_id}',
            path: {
                'ledger_id': ledgerId,
            },
            query: {
                'cluster_id': clusterId,
                'search': search,
                'scenario_id': scenarioId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflows For Ledger With Relations Paginated
     * List cashflows for a specific ledger with categories and tags with pagination, sorting, and optional entity filtering.
     * @param ledgerId
     * @param clusterId
     * @param page
     * @param pageSize
     * @param search
     * @param scenarioId
     * @param orderBy
     * @param sortDirection
     * @param requestBody
     * @returns PaginatedResponse Successful Response
     * @throws ApiError
     */
    public listCashflowsForLedgerWithRelationsPaginated(
        ledgerId: string,
        clusterId: string,
        page: number = 1,
        pageSize: number = 10,
        search: string = '',
        scenarioId?: string,
        orderBy: string = 'date',
        sortDirection: string = 'desc',
        requestBody?: Body_list_cashflows_for_ledger_with_relations_paginated,
    ): CancelablePromise<PaginatedResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/cashflows/list-cashflows-for-ledger-with-relations-paginated/{ledger_id}',
            path: {
                'ledger_id': ledgerId,
            },
            query: {
                'cluster_id': clusterId,
                'page': page,
                'page_size': pageSize,
                'search': search,
                'scenario_id': scenarioId,
                'order_by': orderBy,
                'sort_direction': sortDirection,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cashflow With Relations
     * Create a new cashflow entry with entity links and tag values - unified approach.
     * @param clusterId
     * @param requestBody
     * @returns CashflowRead Successful Response
     * @throws ApiError
     */
    public createCashflowWithRelations(
        clusterId: string,
        requestBody: CashflowCreateWithRelations,
    ): CancelablePromise<CashflowRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/cashflows/create-cashflow-with-relations',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cashflow
     * Update a cashflow with entity links, tag values, and external identifiers.
     * @param cashflowId
     * @param clusterId
     * @param requestBody
     * @returns CashflowRead Successful Response
     * @throws ApiError
     */
    public updateCashflow(
        cashflowId: string,
        clusterId: string,
        requestBody: CashflowUpdateWithRelations,
    ): CancelablePromise<CashflowRead> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/cashflows/update-cashflow/{cashflow_id}',
            path: {
                'cashflow_id': cashflowId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cashflow Entity Association
     * Set or update a single entity association for a cashflow (for inline editing).
     * @param cashflowId
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateCashflowEntityAssociation(
        cashflowId: string,
        clusterId: string,
        requestBody: EntityAssociationData,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/cashflows/update-cashflow-entity-association/{cashflow_id}',
            path: {
                'cashflow_id': cashflowId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cashflow Transaction Type
     * Update a cashflow transaction type.
     * @param transactionTypeId
     * @param clusterId
     * @param requestBody
     * @returns CashflowTransactionTypeRead Successful Response
     * @throws ApiError
     */
    public updateCashflowTransactionType(
        transactionTypeId: string,
        clusterId: string,
        requestBody: CashflowTransactionTypeCreate,
    ): CancelablePromise<CashflowTransactionTypeRead> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/cashflows/update-cashflow-transaction-type/{transaction_type_id}',
            path: {
                'transaction_type_id': transactionTypeId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Cashflow Transaction Type
     * Delete a cashflow transaction type.
     * @param transactionTypeId
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteCashflowTransactionType(
        transactionTypeId: string,
        clusterId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/cashflows/delete-cashflow-transaction-type/{transaction_type_id}',
            path: {
                'transaction_type_id': transactionTypeId,
            },
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cashflow Scenario
     * Update a cashflow scenario.
     * @param scenarioId
     * @param clusterId
     * @param requestBody
     * @returns CashflowScenarioRead Successful Response
     * @throws ApiError
     */
    public updateCashflowScenario(
        scenarioId: string,
        clusterId: string,
        requestBody: CashflowScenarioCreate,
    ): CancelablePromise<CashflowScenarioRead> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/cashflows/update-cashflow-scenario/{scenario_id}',
            path: {
                'scenario_id': scenarioId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cashflow Tag Category
     * Update a cashflow tag category and its tag values.
     * @param categoryId
     * @param clusterId
     * @param requestBody
     * @returns CashflowTagCategoryRead Successful Response
     * @throws ApiError
     */
    public updateCashflowTagCategory(
        categoryId: string,
        clusterId: string,
        requestBody: CashflowTagCategoryCreateWithValues,
    ): CancelablePromise<CashflowTagCategoryRead> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/cashflows/update-cashflow-tag-category/{category_id}',
            path: {
                'category_id': categoryId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Cashflow Tag Category
     * Delete a cashflow tag category.
     * @param categoryId
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteCashflowTagCategory(
        categoryId: string,
        clusterId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/cashflows/delete-cashflow-tag-category/{category_id}',
            path: {
                'category_id': categoryId,
            },
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cashflow External Identifier Type
     * Update a cashflow external identifier type and its ledger associations.
     * @param externalIdentifierTypeId
     * @param clusterId
     * @param requestBody
     * @returns CashflowExternalIdentifierTypeWithLedgers Successful Response
     * @throws ApiError
     */
    public updateCashflowExternalIdentifierType(
        externalIdentifierTypeId: string,
        clusterId: string,
        requestBody: CashflowExternalIdentifierTypeMutate,
    ): CancelablePromise<CashflowExternalIdentifierTypeWithLedgers> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/cashflows/update-cashflow-external-identifier-type/{external_identifier_type_id}',
            path: {
                'external_identifier_type_id': externalIdentifierTypeId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Cashflow External Identifier Type
     * Delete a cashflow external identifier type.
     * @param externalIdentifierTypeId
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteCashflowExternalIdentifierType(
        externalIdentifierTypeId: string,
        clusterId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/cashflows/delete-cashflow-external-identifier-type/{external_identifier_type_id}',
            path: {
                'external_identifier_type_id': externalIdentifierTypeId,
            },
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Export Template
     * Update an export template.
     * @param templateId
     * @param clusterId
     * @param requestBody
     * @returns CashflowExportTemplateRead Successful Response
     * @throws ApiError
     */
    public updateExportTemplate(
        templateId: string,
        clusterId: string,
        requestBody: CashflowExportTemplateUpdate,
    ): CancelablePromise<CashflowExportTemplateRead> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/cashflows/export-templates/{template_id}',
            path: {
                'template_id': templateId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Export Template
     * Delete an export template.
     * @param templateId
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteExportTemplate(
        templateId: string,
        clusterId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/cashflows/export-templates/{template_id}',
            path: {
                'template_id': templateId,
            },
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Ledgers With Cashflows For Entity
     * List all cashflow ledgers that have at least one cashflow linked to this entity.
     * @param entityId
     * @returns CashflowLedgerRead Successful Response
     * @throws ApiError
     */
    public listLedgersWithCashflowsForEntity(
        entityId: string,
    ): CancelablePromise<Array<CashflowLedgerRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/cashflows/ledgers-with-cashflows',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflows For Entity In Ledger Paginated
     * List cashflows for a specific entity within a ledger with pagination, search, and sorting.
     * @param entityId
     * @param ledgerId
     * @param page
     * @param pageSize
     * @param search
     * @param scenarioId
     * @param orderBy
     * @param sortDirection
     * @returns PaginatedResponse Successful Response
     * @throws ApiError
     */
    public listCashflowsForEntityInLedgerPaginated(
        entityId: string,
        ledgerId: string,
        page: number = 1,
        pageSize: number = 10,
        search: string = '',
        scenarioId?: string,
        orderBy: string = 'date',
        sortDirection: string = 'desc',
    ): CancelablePromise<PaginatedResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/cashflows/list-for-ledger-paginated/{ledger_id}',
            path: {
                'entity_id': entityId,
                'ledger_id': ledgerId,
            },
            query: {
                'page': page,
                'page_size': pageSize,
                'search': search,
                'scenario_id': scenarioId,
                'order_by': orderBy,
                'sort_direction': sortDirection,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cashflow Ledger
     * Create a new cashflow ledger for a cluster.
     * @param clusterId
     * @param requestBody
     * @returns CashflowLedgerRead Successful Response
     * @throws ApiError
     */
    public createCashflowLedger(
        clusterId: string,
        requestBody: CashflowLedgerCreateRequest,
    ): CancelablePromise<CashflowLedgerRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/cashflows/create-cashflow-ledger',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflow Ledgers For Cluster
     * List all cashflow ledgers for a cluster.
     * @param clusterId
     * @returns CashflowLedgerRead Successful Response
     * @throws ApiError
     */
    public listCashflowLedgersForCluster(
        clusterId: string,
    ): CancelablePromise<Array<CashflowLedgerRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/cashflows/list-cashflow-ledgers',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Ledger Association Coverage
     * Per-namespace entity-association coverage for a cashflow ledger.
     * @param clusterId
     * @param ledgerId
     * @returns NamespaceCoverage Successful Response
     * @throws ApiError
     */
    public getLedgerAssociationCoverage(
        clusterId: string,
        ledgerId: string,
    ): CancelablePromise<Array<NamespaceCoverage>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/cashflows/ledgers/{ledger_id}/association-coverage',
            path: {
                'cluster_id': clusterId,
                'ledger_id': ledgerId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflow Transaction Types For Cluster
     * List all cashflow transaction types for a cluster.
     * @param clusterId
     * @returns CashflowTransactionTypeRead Successful Response
     * @throws ApiError
     */
    public listCashflowTransactionTypesForCluster(
        clusterId: string,
    ): CancelablePromise<Array<CashflowTransactionTypeRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/cashflows/list-cashflow-transaction-types',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cashflow Transaction Type
     * Create a new cashflow transaction type.
     * @param clusterId
     * @param requestBody
     * @returns CashflowTransactionTypeRead Successful Response
     * @throws ApiError
     */
    public createCashflowTransactionType(
        clusterId: string,
        requestBody: CashflowTransactionTypeCreate,
    ): CancelablePromise<CashflowTransactionTypeRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/cashflows/create-cashflow-transaction-type',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflow Scenarios For Cluster
     * List all cashflow scenarios for a cluster.
     * @param clusterId
     * @returns CashflowScenarioRead Successful Response
     * @throws ApiError
     */
    public listCashflowScenariosForCluster(
        clusterId: string,
    ): CancelablePromise<Array<CashflowScenarioRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/cashflows/list-cashflow-scenarios',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cashflow Scenario
     * Create a new cashflow scenario.
     * @param clusterId
     * @param requestBody
     * @returns CashflowScenarioRead Successful Response
     * @throws ApiError
     */
    public createCashflowScenario(
        clusterId: string,
        requestBody: CashflowScenarioCreate,
    ): CancelablePromise<CashflowScenarioRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/cashflows/create-cashflow-scenario',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflow Tag Categories For Cluster
     * List all cashflow tag categories for a cluster.
     * @param clusterId
     * @returns CashflowTagCategoryRead Successful Response
     * @throws ApiError
     */
    public listCashflowTagCategoriesForCluster(
        clusterId: string,
    ): CancelablePromise<Array<CashflowTagCategoryRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/cashflows/list-cashflow-tag-categories',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cashflow Tag Category
     * Create a new cashflow tag category with initial tag values.
     * @param clusterId
     * @param requestBody
     * @returns CashflowTagCategoryRead Successful Response
     * @throws ApiError
     */
    public createCashflowTagCategory(
        clusterId: string,
        requestBody: CashflowTagCategoryCreateWithValues,
    ): CancelablePromise<CashflowTagCategoryRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/cashflows/create-cashflow-tag-category',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflow External Identifier Types For Cluster
     * List all cashflow external identifier types for a cluster with ledger usage information.
     * @param clusterId
     * @returns CashflowExternalIdentifierTypeWithLedgers Successful Response
     * @throws ApiError
     */
    public listCashflowExternalIdentifierTypesForCluster(
        clusterId: string,
    ): CancelablePromise<Array<CashflowExternalIdentifierTypeWithLedgers>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/cashflows/list-cashflow-external-identifier-types',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cashflow External Identifier Type
     * Create a new cashflow external identifier type with ledger associations.
     * @param clusterId
     * @param requestBody
     * @returns CashflowExternalIdentifierTypeWithLedgers Successful Response
     * @throws ApiError
     */
    public createCashflowExternalIdentifierType(
        clusterId: string,
        requestBody: CashflowExternalIdentifierTypeMutate,
    ): CancelablePromise<CashflowExternalIdentifierTypeWithLedgers> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/cashflows/create-cashflow-external-identifier-type',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Cashflow Import Fields
     * Get field definitions for cashflow imports.
     *
     * This endpoint provides the schema for the import modal:
     * - Core cashflow fields (amount, date, entity, etc.)
     * - Dynamic tag categories for this cluster
     * - Dynamic external identifier types for this cluster
     *
     * Frontend uses this to build the field mapping UI dynamically.
     *
     * Returns:
     * List of field definitions with keys, labels, types, and requirements
     * @param clusterId
     * @param ledgerId
     * @returns FieldDefinition Successful Response
     * @throws ApiError
     */
    public getCashflowImportFields(
        clusterId: string,
        ledgerId: string,
    ): CancelablePromise<Array<FieldDefinition>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/cashflows/ledgers/{ledger_id}/import/fields',
            path: {
                'cluster_id': clusterId,
                'ledger_id': ledgerId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Import Cashflows From File
     * Import cashflows from file data (CSV, Excel) uploaded via the UI.
     *
     * This endpoint:
     * - Receives mapped data from the frontend ImportModal
     * - Uses generic importer for field-level validation
     * - Delegates to existing cashflow importer for complex logic
     *
     * Args:
     * cluster_id: Cluster ID (from auth guard)
     * ledger_id: Target ledger for cashflows
     * request: Import request containing data and options
     *
     * Returns:
     * ImportResult with status, counts, and any errors
     * @param clusterId
     * @param ledgerId
     * @param requestBody
     * @returns ImportResult Successful Response
     * @throws ApiError
     */
    public importCashflowsFromFile(
        clusterId: string,
        ledgerId: string,
        requestBody: CashflowFileImportRequest,
    ): CancelablePromise<ImportResult> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/cashflows/ledgers/{ledger_id}/import-from-file',
            path: {
                'cluster_id': clusterId,
                'ledger_id': ledgerId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Export Columns
     * Get available columns for cashflow ledger export configuration.
     *
     * Returns core columns (fixed) plus dynamic columns based on ledger configuration:
     * - Tag categories: Columns for each tag category defined in the cluster
     * - External identifiers: Columns for each external ID type associated with the ledger
     * @param clusterId
     * @param ledgerId
     * @returns CashflowExportColumnsResponse Successful Response
     * @throws ApiError
     */
    public getExportColumns(
        clusterId: string,
        ledgerId: string,
    ): CancelablePromise<CashflowExportColumnsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/cashflows/ledgers/{ledger_id}/export/columns',
            path: {
                'cluster_id': clusterId,
                'ledger_id': ledgerId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Export Cashflows To Excel
     * Export cashflows from a ledger to an Excel file.
     *
     * The exported file is compatible with the import format, enabling round-trip workflows:
     * download → edit externally → re-import with upsert.
     *
     * Features:
     * - Column headers match import field labels exactly
     * - Includes Cashflow ID column to enable upsert via match_using="lumonic_id"
     * - Tags exported as semicolon-delimited strings
     * - Dates formatted as YYYY-MM-DD
     * - Transaction Type and Scenario use names (not IDs)
     * - Optional column, entity, scenario, and property-based filtering
     *
     * Args:
     * cluster_id: Cluster ID (from auth guard)
     * ledger_id: The ledger to export cashflows from
     * request: Export configuration with optional column, entity, and scenario filters.
     *
     * Returns:
     * Response containing presigned URL for downloading the Excel file (expires in 1 hour)
     * @param clusterId
     * @param ledgerId
     * @param requestBody
     * @returns CashflowExportResponse Successful Response
     * @throws ApiError
     */
    public exportCashflowsToExcel(
        clusterId: string,
        ledgerId: string,
        requestBody?: CashflowExportRequest,
    ): CancelablePromise<CashflowExportResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/cashflows/ledgers/{ledger_id}/export',
            path: {
                'cluster_id': clusterId,
                'ledger_id': ledgerId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Export Templates
     * List all export templates for a ledger, sorted pinned-first then newest.
     * @param clusterId
     * @param ledgerId
     * @returns CashflowExportTemplateRead Successful Response
     * @throws ApiError
     */
    public listExportTemplates(
        clusterId: string,
        ledgerId: string,
    ): CancelablePromise<Array<CashflowExportTemplateRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/cashflows/ledgers/{ledger_id}/export-templates',
            path: {
                'cluster_id': clusterId,
                'ledger_id': ledgerId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Export Template
     * Create a new export template for a ledger.
     * @param clusterId
     * @param ledgerId
     * @param requestBody
     * @returns CashflowExportTemplateRead Successful Response
     * @throws ApiError
     */
    public createExportTemplate(
        clusterId: string,
        ledgerId: string,
        requestBody: CashflowExportTemplateCreate,
    ): CancelablePromise<CashflowExportTemplateRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/cashflows/ledgers/{ledger_id}/export-templates',
            path: {
                'cluster_id': clusterId,
                'ledger_id': ledgerId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
